import { z } from "zod";

/// Workspace-directory primitives, promoted out of
/// features/people/schemas/directory.schema.ts in the C2 deepening
/// (2026-05-23). The picker-row shape and its enum vocabulary are
/// consumed by feedback, kudos, one-on-one, todos, and people itself.
/// people/ keeps the detail-page, stats, and my-reports extensions
/// that build on these primitives.

export const employmentTypeSchema = z.enum(["full_time", "part_time", "contractor", "intern"]);
export type EmploymentType = z.infer<typeof employmentTypeSchema>;

export const workModeSchema = z.enum(["in_office", "hybrid", "remote"]);
export type WorkMode = z.infer<typeof workModeSchema>;

export const membershipChannelSchema = z.enum(["slack", "email", "ms_teams"]);
export type MembershipChannel = z.infer<typeof membershipChannelSchema>;

export const membershipAddedViaSchema = z.enum([
  "signup",
  "invite_in_app",
  "invite_csv",
  "invite_sheet",
  "slack_sync",
]);
export type MembershipAddedVia = z.infer<typeof membershipAddedViaSchema>;

export const directoryRoleSchema = z.enum(["admin", "manager", "member"]);
export type DirectoryRole = z.infer<typeof directoryRoleSchema>;

export const directorySortBySchema = z.enum(["displayName", "joinedAt", "hireDate", "role"]);
export type DirectorySortBy = z.infer<typeof directorySortBySchema>;

export const directorySortDirSchema = z.enum(["asc", "desc"]);
export type DirectorySortDir = z.infer<typeof directorySortDirSchema>;

export const directoryStatusSchema = z.enum(["active", "invited", "all"]);
export type DirectoryStatus = z.infer<typeof directoryStatusSchema>;

export const listDirectoryQuerySchema = z
  .object({
    cursor: z.string().optional(),
    limit: z.number().int().min(1).max(100).default(50),
    sortBy: directorySortBySchema.default("displayName"),
    sortDir: directorySortDirSchema.default("asc"),
    search: z.string().max(100).optional(),
    role: directoryRoleSchema.optional(),
    employmentType: employmentTypeSchema.optional(),
    workMode: workModeSchema.optional(),
    channel: membershipChannelSchema.optional(),
    addedVia: membershipAddedViaSchema.optional(),
    departmentId: z.string().optional(),
    status: directoryStatusSchema.optional(),
  })
  .strict();
export type ListDirectoryQuery = z.infer<typeof listDirectoryQuerySchema>;

const directManagerSchema = z
  .object({
    userId: z.string(),
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
  })
  .nullable();

export const directoryRowSchema = z.object({
  /** "active" = real Membership; "invited" = pending Invitation. */
  status: directoryStatusSchema.extract(["active", "invited"]).default("active"),
  membershipId: z.string(),
  /** Populated for "invited" rows; null for "active" rows. */
  invitationId: z.string().nullable().default(null),
  userId: z.string(),
  displayName: z.string(),
  email: z.string(),
  avatarUrl: z.string().nullable(),
  role: directoryRoleSchema,
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  nickName: z.string().nullable(),
  timezone: z.string().nullish(),
  preferredCurrency: z.string().nullish(),
  title: z.string().nullable(),
  departmentName: z.string().nullable(),
  employmentType: employmentTypeSchema.nullable(),
  workMode: workModeSchema.nullable(),
  location: z.string().nullable(),
  hireDate: z.string().nullable(),
  employeeId: z.string().nullable(),
  channel: membershipChannelSchema,
  isPulseActive: z.boolean(),
  addedVia: membershipAddedViaSchema,
  joinedAt: z.string(),
  directManager: directManagerSchema,
});
export type DirectoryRow = z.infer<typeof directoryRowSchema>;

export const directoryListResponseSchema = z.object({
  rows: z.array(directoryRowSchema),
  nextCursor: z.string().nullable(),
});
export type DirectoryListResponse = z.infer<typeof directoryListResponseSchema>;
